import type { IBorder, ICanvas, IColor, IFont, IImage, ILayout, IPlacement, IRubberStampPreview, IRubberStampResult, ISection, ISpacing, IText, IWizData } from "./Interfaces";

export class Border implements IBorder {
    thickness: number;
    hideThickness: boolean;
    style: string;
    hideStyle: boolean;
    shape: string;
    hideShape: boolean;
    constructor(
        thickness: number,
        hideThickness: boolean,
        style: string,
        hideStyle: boolean,
        shape: string,
        hideShape: boolean
    ) {
        this.thickness = thickness;
        this.hideThickness = hideThickness;
        this.style = style;
        this.hideStyle = hideStyle;
        this.shape = shape;
        this.hideShape = hideShape;
    }
};

export class Spacing implements ISpacing {
    margin: number[];
    hideMargin: boolean;
    padding: number[];
    hidePadding: boolean;
    constructor(
        margin: number[],
        hideMargin: boolean,
        padding: number[],
        hidePadding: boolean
    ) {
        this.margin = margin;
        this.hideMargin = hideMargin;
        this.padding = padding;
        this.hidePadding = hidePadding;
    }
};

export class Font implements IFont {
    name: string;
    hideName: boolean;
    size: number;
    hideSize: boolean;
    decoration: string[];
    hideDecoration: boolean;
    constructor(
        name: string,
        hideName: boolean,
        size: number,
        hideSize: boolean,
        decoration: string[],
        hideDecoration: boolean
    ) {
        this.name = name;
        this.size = size;
        this.decoration = decoration;
        this.hideName = hideName;
        this.hideSize = hideSize;
        this.hideDecoration = hideDecoration;
    }
};

export class LineText implements IText {
    content: string;
    font: Font;
    horizontalAlignment: string;
    spacing: Spacing;
    border: Border;
    isCurved: boolean;
    counterClockwiseDirection: boolean;
    hideFont: boolean;
    hideAlignment: boolean;
    hideSpacing: boolean;
    hideBorder: boolean;
    hideIsCurved: boolean;
    expanded: boolean;

    constructor(
        content: string,
        font: IFont,
        hideFont: boolean,
        horizontalAlignment: string,
        hideAlignment: boolean,
        spacing: ISpacing,
        hideSpacing: boolean,
        border: IBorder,
        hideBorder: boolean,
        isCurved: boolean,
        hideIsCurved: boolean,
        counterClockwiseDirection: boolean,
        expanded: boolean
    ) {
        this.content = content;
        this.font = new Font(font.name, font.hideName, font.size, font.hideSize, font.decoration, font.hideDecoration)
        this.hideFont = hideFont;
        this.horizontalAlignment = horizontalAlignment;
        this.hideAlignment = hideAlignment;
        this.spacing = new Spacing(spacing.margin, spacing.hideMargin, spacing.padding, spacing.hidePadding);
        this.hideSpacing = hideSpacing;
        this.border = new Border(border.thickness, border.hideThickness, border.style, border.hideStyle, border.shape, border.hideShape);
        this.hideBorder = hideBorder;
        this.isCurved = isCurved;
        this.hideIsCurved = hideIsCurved;
        this.counterClockwiseDirection = counterClockwiseDirection;
        this.expanded = expanded;
    }
}

export class Image implements IImage {
    imageId: string;
    usePositionPreset: boolean;
    positionPreset: string[];
    placement: IPlacement;
    constructor(
        imageId: string,
        usePositionPreset: boolean,
        positionPreset: string[],
        placement: IPlacement
    ) {
        this.imageId = imageId;
        this.usePositionPreset = usePositionPreset;
        this.positionPreset = positionPreset;
        this.placement = placement;
    }
};

export class Placement implements IPlacement {
    widthInPercentage: number;
    heightInPercentage: number;
    xPostionInPercentage: number;
    yPostionInPercentage: number;
    constructor(
        widthInPercentage: number,
        heightInPercentage: number,
        xPostionInPercentage: number,
        yPostionInPercentage: number) {
        this.widthInPercentage = widthInPercentage;
        this.heightInPercentage = heightInPercentage;
        this.xPostionInPercentage = xPostionInPercentage;
        this.yPostionInPercentage = yPostionInPercentage;
    }
}

export class Section implements ISection {
    name: string;
    placement: IPlacement;
    border: IBorder;
    hideBorder: boolean;
    spacing: ISpacing;
    hideSpacing: boolean;
    lines: IText[];
    images: IImage[];
    hideSection: boolean;
    constructor(
        name: string,
        placement: IPlacement,
        border: IBorder,
        hideBorder: boolean,
        spacing: ISpacing,
        hideSpacing: boolean,
        lines: IText[],
        images: IImage[],
        hideSection: boolean
    ) {
        this.name = name;
        this.placement = placement;
        this.border = border;
        this.hideBorder = hideBorder;
        this.spacing = spacing;
        this.hideSpacing = hideSpacing;
        this.lines = lines;
        this.images = images;
        this.hideSection = hideSection;
    }
};

export class Canvas implements ICanvas {
    layoutId: string;
    stampCategory: string;
    stampType: string;
    hideStampType: boolean;
    canvasWidth: number;
    canvasHeight: number;
    shape: string;
    sizeInPercentage: number;
    hideHideSizeInPercentage: boolean;
    border: IBorder;
    hideBorder: boolean;
    spacing: ISpacing;
    hideSpacing: boolean;
    colors: IColor[];
    hideColors: boolean;
    lines: IText[];
    images: IImage[];
    sections: ISection[];
    colorComboType: number;
    filter: string;
    colorCombo: IColor[];
    constructor(
        layoutId: string,
        stampCategory: string,
        stampType: string,
        hideStampType: boolean,
        canvasWidth: number,
        canvasHeight: number,
        shape: string,
        sizeInPercentage: number,
        hideHideSizeInPercentage: boolean,
        border: IBorder,
        hideBorder: boolean,
        spacing: ISpacing,
        hideSpacing: boolean,
        colors: IColor[],
        hideColors: boolean,
        lines: IText[],
        images: IImage[],
        sections: ISection[],
        colorComboType: number,
        filter: string,
        colorCombo: IColor[]
    ) {
        this.layoutId = layoutId;
        this.stampCategory = stampCategory;
        this.stampType = stampType;
        this.hideStampType = hideStampType;
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.shape = shape;
        this.sizeInPercentage = sizeInPercentage;
        this.hideHideSizeInPercentage = hideHideSizeInPercentage;
        this.border = border;
        this.hideBorder = hideBorder;
        this.spacing = spacing;
        this.hideSpacing = hideSpacing;
        this.colors = colors;
        this.hideColors = hideColors;
        this.lines = lines;
        this.images = images;
        this.sections = sections;
        this.colorComboType = colorComboType;
        this.filter = filter;
        this.colorCombo = colorCombo;
    }
};

export class Layout implements ILayout {
    id: string;
    name: string;
    previewImage: string;
    constructor(
        id: string,
        name: string,
        previewImage: string
    ) {
        this.id = id;
        this.name = name;
        this.previewImage = previewImage;
    }
};

export class RubberStampPreview implements IRubberStampPreview {
    image: string;
    width: number;
    height: number;
    constructor(
        image: string,
        width: number,
        height: number
    ) {
        this.image = image;
        this.width = width;
        this.height = height;
    }
}

export class RubberStampResult implements IRubberStampResult {
    success: boolean;
    errorTitle: string;
    errorDescription: string;
    productId: number;
    productSKU: string;
    productName: string;
    productWidth: number;
    productHeight: number;
    settingsControlId: string;
    productPicturePath: string;
    productPrice: string;
    productOldPrice: string;
    constructor(
        success: boolean,
        errorTitle: string,
        errorDescription: string,
        productId: number,
        productSKU: string,
        productName: string,
        productWidth: number,
        productHeight: number,
        settingsControlId: string,
        productPicturePath: string,
        productPrice: string,
        productOldPrice: string
    ) {
        this.success = success;
        this.errorTitle = errorTitle;
        this.errorDescription = errorDescription;
        this.productId = productId;
        this.productSKU = productSKU;
        this.productName = productName;
        this.productWidth = productWidth;
        this.productHeight = productHeight;
        this.settingsControlId = settingsControlId;
        this.productPicturePath = productPicturePath;
        this.productPrice = productPrice;
        this.productOldPrice = productOldPrice;
    }
}

export class WizData implements IWizData {
    settingsElementId: string;
    cartItemElementId: string;
    productId: string;
    categoryId: string;
    assetsURI: string;
    constructor(
        outputElementId: string,
        cartItemElementId: string,
        productId: string,
        categoryId: string,
        assetsURI: string
    ) {
        this.settingsElementId = outputElementId;
        this.cartItemElementId = cartItemElementId;
        this.productId = productId;
        this.categoryId = categoryId;
        this.assetsURI = assetsURI;
    }
};

export type FileUploadTrack = {
    file: File;
    status: "edit" | "uploading" | "complete";
    invalid: boolean;
    errorSubject: string | null;
    errorBody: string | null;
};

export class FileUploadProgress {
    displayControl: boolean;
    value: number;
    max: number;
    helperText: string;
    status: "active" | "finished" | "error";
    fileTrack: FileUploadTrack[];

    constructor() {
        this.displayControl = false;
        this.helperText = "";
        this.max = 0;
        this.value = 0;
        this.status = "active";
        this.fileTrack = [];
    }

    showControl(): FileUploadProgress {
        this.displayControl = true;
        return this;
    }

    hideControl(): FileUploadProgress {
        this.displayControl = false;
        return this;
    }

    updateProgress(value: number, max: number, helperText: string): FileUploadProgress {
        this.value = value;
        this.max = max;
        this.helperText = helperText;
        return this;
    }

    updateStatus(
        status: "active" | "finished" | "error",
        helperText: string
    ): FileUploadProgress {
        this.status = status;
        this.helperText = helperText;
        if (this.status === "error") {
            this.updateFileStatus("edit");
        }
        if (this.status === "finished") {
            this.updateFileStatus("complete");
        }
        return this;
    }

    updateFileStatus(status: "edit" | "uploading" | "complete"): FileUploadProgress {
        this.fileTrack = this.fileTrack.map((f: FileUploadTrack) => {
            !f.invalid && (f.status = status);
            return f;
        });
        return this;
    }

    reset(): FileUploadProgress {
        this.displayControl = false;
        this.status = "active";
        this.value = 0;
        this.max = 0;
        this.helperText = "";
        this.fileTrack = [];
        return this;
    }

    removeFile(filename: string): FileUploadProgress {
        this.fileTrack = this.fileTrack.filter((f) => f.file.name != filename);
        return this;
    }

    addFile(file: FileUploadTrack): FileUploadProgress {
        this.fileTrack = [...this.fileTrack, file];
        return this;
    }
}